import { API_BASE } from "@/config/api";
import { fetchWithAuth } from "@/lib/api";
import type { Asset } from "@/schemas";

interface PreferredAssetCandidate {
  asset_id: string;
  exchange?: string | null;
  currency_symbol?: string | null;
  is_primary?: boolean;
}

function isUsCandidate(candidate: PreferredAssetCandidate): boolean {
  const exchange = candidate.exchange?.toUpperCase();
  const currencySymbol = candidate.currency_symbol?.toUpperCase();

  return exchange === "US" || currencySymbol === "USD" || currencySymbol === "$";
}

export function selectPreferredAsset<T extends PreferredAssetCandidate>(
  candidates: T[],
  fallbackAssetId?: string,
): T | undefined {
  if (candidates.length === 0) {
    return undefined;
  }

  return (
    candidates.find((candidate) => candidate.is_primary) ??
    candidates.find((candidate) => isUsCandidate(candidate)) ??
    candidates.find((candidate) => candidate.asset_id === fallbackAssetId) ??
    candidates[0]
  );
}

export function selectPreferredAssetId<T extends PreferredAssetCandidate>(
  candidates: T[],
  fallbackAssetId?: string,
): string | undefined {
  return selectPreferredAsset(candidates, fallbackAssetId)?.asset_id;
}

type SearchAsset = Asset & {
  is_primary?: boolean;
};

export async function resolvePreferredScreenerAssetId(asset: Asset): Promise<string> {
  if (asset.type !== "stock" && asset.type !== "etf") {
    return asset.asset_id;
  }

  const isin = "isin" in asset ? asset.isin?.trim() : undefined;
  const hasIsin = Boolean(isin && isin !== "-");
  const searchQuery = hasIsin ? isin : asset.symbol;

  if (!searchQuery) {
    return asset.asset_id;
  }

  try {
    const searchUrl = new URL(`${API_BASE}/assets/search`);
    searchUrl.searchParams.append("q", searchQuery);
    searchUrl.searchParams.append("asset_types", "stock,etf");
    searchUrl.searchParams.append("search_by", hasIsin ? "isin" : "symbol");

    const response = await fetchWithAuth(searchUrl.toString());
    if (!response.ok) {
      return asset.asset_id;
    }

    const data = await response.json();
    const results: SearchAsset[] = data.results || [];
    const variants = results.filter((candidate) => {
      if (hasIsin && "isin" in candidate) {
        return candidate.isin === isin;
      }

      return candidate.symbol === asset.symbol;
    });

    return selectPreferredAssetId(variants, asset.asset_id) ?? asset.asset_id;
  } catch {
    return asset.asset_id;
  }
}
